import { InspectionTemplateColumn } from "@/shared/models/Inspection";
import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	Inspection,
	InspectionDetail,
	InspectionHeader,
	InspectionRow,
	InspectionRowDefect,
	InspectionRowDraft,
	InspectionValue,
} from "../models/Inspection";

const moment = getMomentTz();

export const getInspections = async (
	params: {
		shiftId?: number | null;
		assetId?: number | null;
		inspectionId?: number | null;
	},
	signal?: AbortSignal,
): Promise<Inspection[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "InspectionId", value: params.inspectionId ?? null },
		{ name: "ShiftId", value: params.shiftId ?? null },
		{ name: "AssetId", value: params.assetId ?? null },
	];

	const resp = await apiService.callV2(
		"[M1].[GetInspections]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return (get(resp, "data.tables[0].rows", []) as Inspection[]).map(
			(row) => ({
				...row,
				ProductionDate: row.ProductionDate
					? moment.utc(row.ProductionDate).toDate()
					: null,
				ShiftStart: row.ShiftStart ? moment.utc(row.ShiftStart).toDate() : null,
				ShiftEnd: row.ShiftEnd ? moment.utc(row.ShiftEnd).toDate() : null,
				LastRowTimestamp: row.LastRowTimestamp
					? moment.utc(row.LastRowTimestamp).toDate()
					: null,
			}),
		);
	} else {
		throw new Error(resp.message || "Error fetching inspections");
	}
};

export const getInspection = async (
	params: { inspectionId: number },
	signal?: AbortSignal,
): Promise<InspectionDetail> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "InspectionId", value: params.inspectionId },
	];

	const resp = await apiService.callV2(
		"[M1].[GetInspection]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});
		const header = get(tables, "0.rows.0", null) as InspectionHeader | null;

		return {
			inspection: header
				? {
						...header,
						ProductionDate: header.ProductionDate
							? moment.utc(header.ProductionDate).toDate()
							: null,
						ShiftStart: header.ShiftStart
							? moment.utc(header.ShiftStart).toDate()
							: null,
						ShiftEnd: header.ShiftEnd
							? moment.utc(header.ShiftEnd).toDate()
							: null,
					}
				: null,
			columns: get(tables, "1.rows", []) as InspectionTemplateColumn[],
			rows: (get(tables, "2.rows", []) as InspectionRow[]).map((row) => ({
				...row,
				RowTimestamp: row.RowTimestamp
					? moment.utc(row.RowTimestamp).toDate()
					: null,
				CreationTime: row.CreationTime
					? moment.utc(row.CreationTime).toDate()
					: null,
				UpdatedAt: row.UpdatedAt ? moment.utc(row.UpdatedAt).toDate() : null,
			})),
			values: (get(tables, "3.rows", []) as InspectionValue[]).map((value) => ({
				...value,
				UpdatedAt: value.UpdatedAt
					? moment.utc(value.UpdatedAt).toDate()
					: null,
			})),
			defects: get(tables, "4.rows", []) as InspectionRowDefect[],
		};
	} else {
		throw new Error(resp.message || "Error fetching the inspection");
	}
};

export const upsertInspection = async (params: {
	inspectionId?: number | null;
	templateId?: number | null;
	sectionId?: number | null;
	shiftId?: number | null;
	userId?: number | null;
	comments?: string | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "InspectionId", value: params.inspectionId ?? null },
		{ name: "TemplateId", value: params.templateId ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "ShiftId", value: params.shiftId ?? null },
		{ name: "Comments", value: params.comments ?? null },
	];

	if (params.userId) parameters.push({ name: "UserId", value: params.userId });

	const resp = await apiService.callV2("[M1].[UpsertInspection]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving the inspection");
	}
};

export const upsertInspectionRow = async (draft: InspectionRowDraft) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "RowId", value: draft.RowId },
		{ name: "InspectionId", value: draft.InspectionId },
		{
			name: "RowTimestamp",
			value: draft.RowTimestamp ? draft.RowTimestamp.toISOString() : null,
		},
		{ name: "HeatId", value: draft.HeatId },
		{ name: "ValuesJson", value: JSON.stringify(draft.Values) },
		{ name: "DefectsJson", value: JSON.stringify(draft.Defects) },
	];

	const resp = await apiService.callV2(
		"[M1].[UpsertInspectionRow]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving the reading");
	}
};

export const deleteInspectionRow = async (rowId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "RowId", value: rowId }];

	const resp = await apiService.callV2(
		"[M1].[DeleteInspectionRow]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting the reading");
	}
};

export const deleteInspection = async (inspectionId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "InspectionId", value: inspectionId },
	];

	const resp = await apiService.callV2("[M1].[DeleteInspection]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting the inspection sheet");
	}
};
